/**
 * Write-state model for 重点保障中心 rows (#2658).
 *
 * The operator's complaint was "I changed it and nothing happened": a write
 * could take seconds and the table kept the old value with no sign of life.
 * Every write now moves its row through saving → saved (with where it has
 * taken effect) or error (with the server's message), and the texts below say
 * only what the gateway code guarantees.
 */
import type {
  GuaranteedPropagationState,
  GuaranteedScope,
  PatchGuaranteedServiceRequest,
  RoutingCacheConvergence,
} from '@nadoutong/contracts';
import { canonicalGuaranteedMode, canonicalGuaranteedOverride } from '@nadoutong/contracts';
import type { I18nKey, TParams } from '../../i18n/index.ts';
import { convergenceOutcome, convergenceWindowSeconds } from '../routing-dr/convergence.ts';
import { fallbackLabelKey, modeLabelKey } from './modes.ts';
import { cutoffSecondsText, storedCutoffMs } from './noResponseCutoff.ts';

type Translate = (key: I18nKey, params?: TParams) => string;

/**
 * How long Shanghai may keep serving the previous value after its database
 * holds the new row. Downlink apply does not bump CN's routing generation, so
 * CN caches expire by TTL only:
 * - candidate sets (ultimate fallback): 10 s isolate + 10 s shared Tair
 *   (`TENANT_ROUTING_CANDIDATE_CACHE_TTL_SECONDS`, gateway
 *   `src/generation/routing.ts`) → ≤ 20 s;
 * - SKU-layer probe / chain / Jev flags: 30 s isolate cache
 *   (`TARGET_PROBE_SETTINGS_CACHE_TTL_MS`, gateway
 *   `src/routing/target-probes/settings.ts`) → ≤ 30 s.
 * Mirrored, not imported: apps may not import another app's source.
 */
export const CN_EFFECT_BOUND_SECONDS: Readonly<Record<GuaranteedScope, number>> = {
  sku: 30,
  org_sku: 20,
};

/** Same 30 s flag cache bounds Tokyo when the generation publish was partial. */
const SKU_FLAGS_TTL_SECONDS = 30;

export type CnConfirmation =
  | { readonly kind: 'waiting'; readonly last: GuaranteedPropagationState | null }
  | { readonly kind: 'confirmed' }
  /** Deadline passed; `last` is the final CN state seen, so the copy names only that. */
  | { readonly kind: 'timeout'; readonly last: GuaranteedPropagationState }
  | { readonly kind: 'not_configured' };

export type RowWriteStatus =
  | { readonly phase: 'saving'; readonly label: string; readonly pending: PatchGuaranteedServiceRequest | null }
  | {
    readonly phase: 'saved';
    readonly op: 'write' | 'delete';
    readonly scope: GuaranteedScope;
    readonly cache: RoutingCacheConvergence | null;
    /** Server-stamped `updated_at` of the write; null for a delete. */
    readonly updatedAt: string | null;
    /** Client clock when the response arrived; the countdown runs from here. */
    readonly receivedAtMs: number;
    readonly cn: CnConfirmation;
  }
  | { readonly phase: 'error'; readonly message: string };

export function isRowBusy(status: RowWriteStatus | undefined): boolean {
  return status?.phase === 'saving';
}

function modeText(t: Translate, value: unknown): string {
  const mode = canonicalGuaranteedMode(value);
  return mode === null ? String(value) : t(modeLabelKey(mode));
}

function fallbackText(t: Translate, value: unknown): string {
  const overlay = canonicalGuaranteedOverride(value);
  if (overlay === 'inherit') return t('guaranteed.inherit');
  return overlay === null ? String(value) : t(fallbackLabelKey(overlay));
}

/** Numeric fields in the order the write label names them. */
const NUMBER_FIELDS: ReadonlyArray<readonly [string, I18nKey]> = [
  ['probe_interval_minutes', 'guaranteed.write.field.probe_interval'],
  ['probe_shots_per_round', 'guaranteed.write.field.probe_shots'],
  ['probe_trip_failed_rounds', 'guaranteed.write.field.probe_fail_rounds'],
  ['probe_recover_ok_rounds', 'guaranteed.write.field.probe_pass_rounds'],
  ['probe_shot_spacing_seconds', 'guaranteed.write.field.probe_spacing'],
  ['probe_shot_timeout_seconds', 'guaranteed.write.field.probe_timeout'],
  ['probe_confirm_after_seconds', 'guaranteed.write.field.probe_confirm'],
  ['probe_slow_ms', 'guaranteed.write.field.probe_slow'],
  ['probe_quality_window_minutes', 'guaranteed.write.field.probe_quality_window'],
  ['probe_quality_min_shots', 'guaranteed.write.field.probe_quality_min'],
  ['probe_quality_trip_pct', 'guaranteed.write.field.probe_quality_trip'],
  ['probe_quality_recover_pct', 'guaranteed.write.field.probe_quality_recover'],
  ['probe_quality_min_bad_shots', 'guaranteed.write.field.probe_quality_min_bad'],
  ['probe_quality_recover_window_minutes', 'guaranteed.write.field.probe_quality_recover_window'],
  ['probe_alert_interval_seconds', 'guaranteed.write.field.probe_alert_interval'],
  ['probe_alert_calm_minutes', 'guaranteed.write.field.probe_alert_calm'],
  ['probe_alert_min_failures', 'guaranteed.write.field.probe_alert_failures'],
  ['probe_alert_min_fail_pct', 'guaranteed.write.field.probe_alert_fail_pct'],
  ['probe_alert_first_byte_late_ms', 'guaranteed.write.field.probe_alert_first_byte_late'],
  ['probe_burst_wave_shots', 'guaranteed.write.field.probe_wave_shots'],
  ['probe_burst_wave_every_seconds', 'guaranteed.write.field.probe_wave_every'],
  ['probe_burst_wave_fail_shots', 'guaranteed.write.field.probe_wave_fail'],
  ['probe_burst_waves_to_trip', 'guaranteed.write.field.probe_waves_to_trip'],
  ['probe_burst_max_seconds', 'guaranteed.write.field.probe_war_max'],
  ['probe_burst_exit_good_waves', 'guaranteed.write.field.probe_exit_good'],
  ['probe_war_cooldown_base_minutes', 'guaranteed.write.field.probe_cool_base'],
  ['probe_war_cooldown_factor', 'guaranteed.write.field.probe_cool_factor'],
  ['probe_war_cooldown_max_minutes', 'guaranteed.write.field.probe_cool_max'],
  ['probe_war_backoff_reset_minutes', 'guaranteed.write.field.probe_cool_reset'],
];

/** 「探针模式 → 摘除」 etc.; several fields join with 「，」. */
export function describePatch(t: Translate, body: PatchGuaranteedServiceRequest): string {
  const parts: string[] = [];
  const add = (key: I18nKey, value: string): void => {
    parts.push(t('guaranteed.write.field_value', { field: t(key), value }));
  };
  const record = new Map<string, unknown>(Object.entries(body));
  if (record.get('probe_mode') !== undefined) add('guaranteed.write.field.probe_mode', modeText(t, record.get('probe_mode')));
  if (record.get('probe_shape') !== undefined) add('guaranteed.write.field.probe_shape', String(record.get('probe_shape')));
  for (const [field, key] of NUMBER_FIELDS) {
    const value = record.get(field);
    if (value !== undefined) add(key, String(value));
  }
  if (record.get('chain_trip_mode') !== undefined) add('guaranteed.write.field.chain', modeText(t, record.get('chain_trip_mode')));
  if (record.get('ultimate_fallback') !== undefined) {
    add('guaranteed.write.field.fallback', fallbackText(t, record.get('ultimate_fallback')));
  }
  if (record.get('jev_judge') !== undefined) add('guaranteed.write.field.jev', modeText(t, record.get('jev_judge')));
  if (record.get('jev_trip_min_hits') !== undefined) {
    add('guaranteed.write.field.jev_min_hits', String(record.get('jev_trip_min_hits')));
  }
  if (record.get('jev_trip_window_seconds') !== undefined) {
    add('guaranteed.write.field.jev_window', String(record.get('jev_trip_window_seconds')));
  }
  if (record.get('no_response_cutoff_ms') !== undefined) {
    const ms = storedCutoffMs(record.get('no_response_cutoff_ms'));
    add('guaranteed.write.field.no_response_cutoff', ms === null ? t('guaranteed.cutoff.off') : cutoffSecondsText(ms));
  }
  if (record.get('note') !== undefined) parts.push(t('guaranteed.write.field.note'));
  return parts.join(t('guaranteed.write.field_join'));
}

/** Seconds left on the Tokyo countdown; 0 means in effect. */
export function tokyoSecondsLeft(status: Extract<RowWriteStatus, { phase: 'saved' }>, nowMs: number): number | null {
  const outcome = convergenceOutcome(status.cache);
  if (outcome === 'unknown') return null;
  if (outcome === 'immediate') return 0;
  const windowSeconds = convergenceWindowSeconds(status.cache, status.updatedAt, status.receivedAtMs);
  const bound = status.scope === 'sku' ? Math.max(windowSeconds, SKU_FLAGS_TTL_SECONDS) : windowSeconds;
  return Math.max(0, bound - Math.floor((nowMs - status.receivedAtMs) / 1000));
}

export function tokyoLine(t: Translate, status: Extract<RowWriteStatus, { phase: 'saved' }>, nowMs: number): string {
  const outcome = convergenceOutcome(status.cache);
  if (outcome === 'unknown') return t('guaranteed.write.jp.unknown');
  if (outcome === 'immediate') return t('guaranteed.write.jp.immediate');
  const left = tokyoSecondsLeft(status, nowMs) ?? 0;
  return left > 0
    ? t('guaranteed.write.jp.degraded', { seconds: String(left) })
    : t('guaranteed.write.jp.degraded_done');
}

export function shanghaiLine(t: Translate, status: Extract<RowWriteStatus, { phase: 'saved' }>): string {
  const seconds = String(CN_EFFECT_BOUND_SECONDS[status.scope]);
  switch (status.cn.kind) {
    case 'confirmed':
      return status.op === 'delete'
        ? t('guaranteed.write.cn.removed', { seconds })
        : t('guaranteed.write.cn.received', { seconds });
    case 'timeout':
      return status.cn.last === 'unreachable'
        ? t('guaranteed.write.cn.timeout_unreachable')
        : t('guaranteed.write.cn.timeout_lagging');
    case 'not_configured':
      return t('guaranteed.write.cn.not_configured');
    case 'waiting':
      return status.cn.last === 'unreachable'
        ? t('guaranteed.write.cn.unreachable')
        : t('guaranteed.write.cn.waiting');
  }
}

/**
 * The value a control should show while its row is saving: the target it is
 * being written to. `undefined` when this write does not touch the field.
 * Callers validate it with the same guard the control uses, so a pending value
 * never outlives its write: once the row leaves `saving` this returns nothing.
 */
export function pendingField(
  status: RowWriteStatus | undefined,
  field: string,
): unknown {
  if (status?.phase !== 'saving' || status.pending === null) return undefined;
  return new Map<string, unknown>(Object.entries(status.pending)).get(field);
}
